import { CollapseHelpText } from '@/components/help/CollapseHelpText';
import { FAQ_QUESTION_PHONE_BINDING } from '@/app/routes';
import { FaqInfoDTO } from '@/types/help/FaqInfoDTO';
import { Grid, Skeleton, Theme, Typography, useMediaQuery } from '@mui/material';
import { useGetClientFaqQuery } from '@/services/api/helpApiSlice';
import { useLocation } from 'react-router-dom';
import React, { FC, ReactElement, useEffect, useRef } from 'react';

/**
 * Titles the `?question=` identifiers resolve to.
 * The text itself lives in `clientFaq.json`, which is served as content,
 * so a reworded entry costs the link its expansion and nothing else.
 */
export const QUESTION_TITLES: Record<string, string> = {
  [FAQ_QUESTION_PHONE_BINDING]: 'Как привязать номер телефона?',
};

export const FaqBlock: FC = (): ReactElement => {
  const { data: faqContent, isFetching } = useGetClientFaqQuery();

  const sendLetterBlockRef = useRef<HTMLDivElement>(null);
  const accountQuestionRef = useRef<HTMLDivElement>(null);

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  const { search } = useLocation();
  const params: URLSearchParams = new URLSearchParams(search);

  const scroll: string | null = params.get('scroll');
  const question: string | null = params.get('question');
  const questionTitle: string | undefined =
    question && Object.prototype.hasOwnProperty.call(QUESTION_TITLES, question) ? QUESTION_TITLES[question] : undefined;

  useEffect((): void => {
    if (sendLetterBlockRef?.current && scroll) {
      sendLetterBlockRef.current.scrollIntoView({ block: 'end' });
    }
  }, [sendLetterBlockRef, scroll, isFetching]);

  useEffect((): void => {
    if (accountQuestionRef?.current && questionTitle) {
      accountQuestionRef.current.scrollIntoView({ block: 'start' });
      accountQuestionRef.current.focus();
    }
  }, [accountQuestionRef, questionTitle, isFetching]);

  return (
    <Grid container={true} direction={'column'} sx={{ maxWidth: '750px' }}>
      {isMobile && (
        <Grid item={true} mb={3}>
          <Typography variant={'h1'}>{'Часто задаваемые вопросы'}</Typography>
        </Grid>
      )}
      <Grid
        item={true}
        sx={{
          background: (theme: Theme) => theme.colors.grayBackground,
          borderRadius: '6px',
          padding: '4px 8px',
          marginBottom: '12px',
        }}>
        <Typography variant={'h1'} fontSize={20}>
          {'Отправка письма'}
        </Typography>
      </Grid>
      {isFetching ? (
        <Grid container={true} direction={'column'} rowSpacing={2}>
          <Grid item={true}>
            <Skeleton variant={'rectangular'} width={750} height={40} />
          </Grid>
          <Grid item={true}>
            <Skeleton variant={'rectangular'} width={750} height={40} />
          </Grid>
          <Grid item={true}>
            <Skeleton variant={'rectangular'} width={750} height={40} />
          </Grid>
        </Grid>
      ) : (
        <div ref={sendLetterBlockRef}>
          {faqContent &&
            faqContent.sendLetter.map((question: FaqInfoDTO, index: number): ReactElement => {
              return (
                <Grid item={true} key={index}>
                  <CollapseHelpText tittle={question.tittle} hide={!(scroll && index === Number(scroll))}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: question.body,
                      }}
                    />
                  </CollapseHelpText>
                </Grid>
              );
            })}
        </div>
      )}
      <Grid
        item={true}
        sx={{
          background: (theme: Theme): string => theme.colors.grayBackground,
          borderRadius: '6px',
          padding: '4px 8px',
          marginTop: '24px',
          marginBottom: '12px',
        }}>
        <Typography variant={'h1'} fontSize={20}>
          {'Личный кабинет'}
        </Typography>
      </Grid>
      {isFetching ? (
        <Grid container={true} direction={'column'} rowSpacing={2}>
          <Grid item={true}>
            <Skeleton variant={'rectangular'} width={750} height={40} />
          </Grid>
          <Grid item={true}>
            <Skeleton variant={'rectangular'} width={750} height={40} />
          </Grid>
          <Grid item={true}>
            <Skeleton variant={'rectangular'} width={750} height={40} />
          </Grid>
        </Grid>
      ) : (
        faqContent &&
        faqContent.account.map((item: FaqInfoDTO, index: number): ReactElement => {
          const isTarget: boolean = Boolean(questionTitle) && item.tittle === questionTitle;
          return (
            <Grid
              item={true}
              key={index}
              ref={isTarget ? accountQuestionRef : undefined}
              tabIndex={isTarget ? -1 : undefined}
              sx={{ ':focus': { outline: 'none' } }}>
              <CollapseHelpText tittle={item.tittle} hide={!isTarget}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: item.body,
                  }}
                />
              </CollapseHelpText>
            </Grid>
          );
        })
      )}
    </Grid>
  );
};
